import { type ReactNode, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { ChartFrame } from '../../../components/chart/chart-frame';
import { DrillDownPanel } from '../../../components/chart/drill-down-panel';
import { FLOW_VAR } from '../../../components/chart/rag';
import { errorMessage } from '../../../lib/error-message';
import { type ReportRange, reportApi, reportKeys } from '../api';

/**
 * Throughput — số task hoàn thành mỗi tuần (spec 16.2).
 *
 * Gom theo tuần, không theo ngày: throughput theo ngày của một đội nhỏ là một
 * hàng số 0 và 1, không đọc ra xu hướng nào.
 *
 * Click vào một cột mở danh sách task **hoàn thành trong ngày đó** — với cột
 * tuần thì đó là ngày thứ Hai đầu tuần, và bảng drill-down nói rõ mốc ngày nó
 * đang hiện để người đọc không phải đoán.
 */
export function ThroughputChart({
  projectId,
  range,
}: {
  projectId: string;
  range: ReportRange;
}): ReactNode {
  const { t } = useTranslation();
  const [bucketDate, setBucketDate] = useState<string | null>(null);

  const throughput = useQuery({
    queryKey: reportKeys.throughput(projectId, range, 'week'),
    queryFn: () => reportApi.throughput(projectId, range, 'week'),
  });

  const drill = useQuery({
    queryKey: reportKeys.drillDown(
      projectId,
      'completed_on',
      bucketDate ?? '',
      '',
      range.asOf ?? '',
    ),
    queryFn: () =>
      reportApi.drillDown(projectId, {
        metric: 'completed_on',
        bucket: bucketDate!,
        ...(range.asOf ? { asOf: range.asOf } : {}),
      }),
    enabled: bucketDate !== null,
  });

  const buckets = throughput.data?.buckets ?? [];

  return (
    <ChartFrame
      title={t('reports.throughputTitle')}
      help="throughput"
      subtitle={t('reports.throughputSubtitle')}
      asOf={range.asOf ? new Date(range.asOf) : undefined}
      loading={throughput.isPending}
      error={throughput.isError ? errorMessage(throughput.error, t) : null}
      onRetry={() => void throughput.refetch()}
      empty={
        throughput.data && buckets.every((bucket) => bucket.count === 0)
          ? { title: t('reports.noCompleted'), description: t('reports.noCompletedHelp') }
          : null
      }
      drillDown={
        bucketDate ? (
          <DrillDownPanel
            result={drill.data ?? null}
            loading={drill.isPending}
            onClose={() => setBucketDate(null)}
          />
        ) : null
      }
    >
      <div className="h-48 w-full">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={buckets}
            onClick={(state: { activeLabel?: string | number | undefined }) => {
              if (typeof state?.activeLabel === 'string') setBucketDate(state.activeLabel);
            }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
            <XAxis dataKey="date" tick={{ fontSize: 11 }} stroke="var(--color-border)" />
            <YAxis allowDecimals={false} tick={{ fontSize: 11 }} stroke="var(--color-border)" />
            <Tooltip
              contentStyle={{
                background: 'var(--color-background)',
                border: '1px solid var(--color-border)',
                fontSize: 12,
              }}
            />
            <Bar dataKey="count" name={t('reports.completedCount')} fill={FLOW_VAR.done} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </ChartFrame>
  );
}
